import B2BPartnersPanel from "@/components/admin/B2BPartnersPanel";
import SuppliersPanel from "@/components/admin/SuppliersPanel";
import SupplierRequestsPanel from "@/components/admin/SupplierRequestsPanel";
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Handshake, Trash2, Mail, MessageCircle, ExternalLink, Filter,
  PhoneCall, UserCheck, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import useConfirmDialog from "@/lib/useConfirmDialog";

const FLEET_LABEL = {
  boats:           "Boats · Charter · Yachts",
  quads_scooters:  "Quads & Scooters",
  rent_a_car:      "Rent-a-Car",
  villas_stays:    "Villas & Stays",
  activities:      "Activities",
  other:           "Other",
};

const STATUS_OPTIONS = ["new", "contacted", "onboarded", "declined"];
const STATUS_COLOR = {
  new:        "bg-[#C5A059]/15 text-[#C5A059] border-[#C5A059]/30",
  contacted:  "bg-blue-500/15 text-blue-300 border-blue-500/30",
  onboarded:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  declined:   "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

function inCurrentMonth(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getUTCFullYear() === now.getUTCFullYear() && d.getUTCMonth() === now.getUTCMonth();
}

const FLEET_TO_CATEGORY = {
  boats: "boats", quads_scooters: "quads_scooters", rent_a_car: "rent_a_car",
  villas_stays: "other", activities: "activities", other: "other",
};

export default function AdminSuppliers() {
  const navigate = useNavigate();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterFleet, setFilterFleet] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});
  const [onboardPrefill, setOnboardPrefill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/partner-applications");
      setApplications(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("juke_admin_token");
        navigate("/admin");
      } else {
        toast.error("Could not load applications");
      }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("juke_admin_token")) { navigate("/admin"); return; }
    load();
  }, [load, navigate]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/partner-applications/${id}`, { status });
      setApplications((list) => list.map((i) => (i.id === id ? { ...i, ...data } : i)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const saveNotes = async (id) => {
    const admin_notes = notesDraft[id] ?? "";
    try {
      const { data } = await api.patch(`/admin/partner-applications/${id}`, { admin_notes });
      setApplications((list) => list.map((i) => (i.id === id ? { ...i, ...data } : i)));
      toast.success("Notes saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save notes");
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this supplier application?",
      description: "The applicant's contact details and pitch will be permanently removed. This cannot be undone.",
      confirmLabel: "Delete application",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/partner-applications/${id}`);
      setApplications((list) => list.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const acceptOnboard = async (a) => {
    await updateStatus(a.id, "onboarded");
    setOnboardPrefill({
      business_name: a.company_name,
      contact_person: a.contact_person,
      email: a.email,
      phone: a.whatsapp,
      category: FLEET_TO_CATEGORY[a.fleet_type] || "other",
      notes: a.pitch || "",
      source_application_id: a.id,
    });
    toast.info("Dovršite profil suppliera — možete odmah poslati i ugovor");
  };

  const filtered = useMemo(() => applications.filter((a) => {
    if (filterStatus !== "all" && (a.status || "new") !== filterStatus) return false;
    if (filterFleet !== "all" && a.fleet_type !== filterFleet) return false;
    return true;
  }), [applications, filterStatus, filterFleet]);

  const kpis = useMemo(() => {
    const total = applications.length;
    const month = applications.filter((a) => inCurrentMonth(a.created_at)).length;
    const onboarded = applications.filter((a) => a.status === "onboarded").length;
    const perFleet = {};
    for (const a of applications) {
      const k = a.fleet_type || "other";
      perFleet[k] = (perFleet[k] || 0) + 1;
    }
    return { total, month, onboarded, perFleet };
  }, [applications]);

  return (
    <div data-testid="admin-suppliers-page" className="min-h-screen bg-[#050505] text-zinc-100">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/admin/dashboard"
            data-testid="suppliers-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-[#C5A059] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to dashboard
          </Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Supplier Applications</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KpiCard label="All time" value={kpis.total} eyebrow="Total applications" gold />
          <KpiCard label="This month" value={kpis.month} eyebrow="New this month" />
          <KpiCard label="Onboarded" value={kpis.onboarded} eyebrow="Signed & live" icon={<Handshake className="w-4 h-4" />} />
          <KpiCard label="Fleet mix"
                   value={Object.keys(kpis.perFleet).length}
                   eyebrow={Object.entries(kpis.perFleet)
                     .sort((a, b) => b[1] - a[1])
                     .slice(0, 2)
                     .map(([k, v]) => `${(FLEET_LABEL[k] || k).split(" ")[0]}: ${v}`)
                     .join("  ·  ") || "—"} />
        </div>

        <div className="flex flex-wrap gap-2 items-center mb-5 text-xs">
          <span className="inline-flex items-center gap-1 uppercase tracking-[0.2em] text-zinc-500 mr-1">
            <Filter className="w-3.5 h-3.5" /> Fleet
          </span>
          {["all", ...Object.keys(FLEET_LABEL)].map((s) => (
            <button
              key={`fl-${s}`}
              data-testid={`filter-fleet-${s}`}
              onClick={() => setFilterFleet(s)}
              className={`px-3 h-9 rounded-full border uppercase tracking-[0.15em] transition-colors ${
                filterFleet === s
                  ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5"
                  : "border-white/10 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {s === "all" ? "All" : (FLEET_LABEL[s] || s).split(" ")[0]}
            </button>
          ))}
          <span className="inline-flex items-center gap-1 uppercase tracking-[0.2em] text-zinc-500 ml-4 mr-1">
            <Filter className="w-3.5 h-3.5" /> Status
          </span>
          {["all", ...STATUS_OPTIONS].map((s) => (
            <button
              key={`st-${s}`}
              data-testid={`filter-status-${s}`}
              onClick={() => setFilterStatus(s)}
              className={`px-3 h-9 rounded-full border uppercase tracking-[0.15em] transition-colors ${
                filterStatus === s
                  ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5"
                  : "border-white/10 text-zinc-400 hover:text-zinc-100"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-white/10 bg-[#0d0d0d] overflow-hidden">
          <div className="hidden lg:grid grid-cols-12 px-6 py-4 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10">
            <div className="col-span-4">Company</div>
            <div className="col-span-3">Fleet type</div>
            <div className="col-span-3">Received</div>
            <div className="col-span-2 text-right">Status</div>
          </div>

          {loading ? (
            <p className="p-8 text-center text-zinc-500 text-sm">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="p-8 text-center text-zinc-500 text-sm">No applications match the current filters.</p>
          ) : (
            filtered.map((a) => {
              const status = a.status || "new";
              const isOpen = expandedId === a.id;
              return (
                <div
                  key={a.id}
                  data-testid={`supplier-row-${a.id}`}
                  className="border-b border-white/5 last:border-0"
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setExpandedId(isOpen ? null : a.id);
                      if (!isOpen) setNotesDraft((d) => ({ ...d, [a.id]: a.admin_notes || "" }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpandedId(isOpen ? null : a.id);
                        if (!isOpen) setNotesDraft((d) => ({ ...d, [a.id]: a.admin_notes || "" }));
                      }
                    }}
                    className="w-full px-6 py-4 grid grid-cols-1 lg:grid-cols-12 gap-2 items-center text-left hover:bg-white/[0.02] transition-colors cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-[#C5A059]/60"
                  >
                    <div className="lg:col-span-4">
                      <p className="text-white text-sm font-medium">{a.company_name}</p>
                      <p className="text-xs text-zinc-500">{a.contact_person} · {a.region || "—"}</p>
                    </div>
                    <div className="lg:col-span-3 text-sm text-zinc-300">
                      {FLEET_LABEL[a.fleet_type] || a.fleet_type}
                      {a.fleet_size && (
                        <span className="text-xs text-zinc-500 ml-2">· {a.fleet_size}</span>
                      )}
                    </div>
                    <div className="lg:col-span-3 text-sm text-zinc-500">{fmtDateTime(a.created_at)}</div>
                    <div className="lg:col-span-2 flex lg:justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <span
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`supplier-status-${a.id}`}
                            className={`px-3 py-1 rounded-full border text-[10px] uppercase tracking-[0.2em] cursor-pointer ${STATUS_COLOR[status]}`}
                          >
                            {status}
                          </span>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                          className="bg-[#0d0d0d] border-white/10 text-zinc-100"
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <DropdownMenuItem
                              key={s}
                              data-testid={`supplier-status-set-${a.id}-${s}`}
                              onClick={() => updateStatus(a.id, s)}
                              className="text-xs uppercase tracking-[0.2em] cursor-pointer"
                            >
                              {s}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="bg-[#080808] border-t border-white/5 px-6 py-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Contact</p>
                        <div className="space-y-1 text-sm">
                          <p className="text-zinc-200">{a.contact_person}</p>
                          <a href={`mailto:${a.email}`} className="text-zinc-400 hover:text-[#C5A059] inline-flex items-center gap-2 block">
                            <Mail className="w-3.5 h-3.5" /> {a.email}
                          </a>
                          <a
                            href={`https://wa.me/${(a.whatsapp || "").replace(/[^0-9]/g, "")}`}
                            target="_blank" rel="noreferrer"
                            className="text-zinc-400 hover:text-[#C5A059] inline-flex items-center gap-2 block"
                          >
                            <MessageCircle className="w-3.5 h-3.5" /> {a.whatsapp}
                          </a>
                          {a.website && (
                            <a
                              href={a.website.startsWith("http") ? a.website : `https://${a.website}`}
                              target="_blank" rel="noreferrer"
                              className="text-zinc-400 hover:text-[#C5A059] inline-flex items-center gap-2 block truncate"
                            >
                              <ExternalLink className="w-3.5 h-3.5" /> {a.website}
                            </a>
                          )}
                        </div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-5 mb-2">Pitch</p>
                        <p className="text-sm text-zinc-300 whitespace-pre-wrap">
                          {a.pitch || <span className="text-zinc-600 italic">No pitch provided.</span>}
                        </p>
                      </div>

                      <div className="lg:col-span-2">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Admin notes (internal)</p>
                        <Textarea
                          data-testid={`supplier-admin-notes-${a.id}`}
                          value={notesDraft[a.id] ?? ""}
                          onChange={(e) => setNotesDraft((d) => ({ ...d, [a.id]: e.target.value }))}
                          rows={5}
                          placeholder="Signed onboarding call for X, commission split 15%, needs contract…"
                          className="bg-zinc-900 border-white/10 text-sm"
                        />
                        <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                            {a.updated_at ? `Last updated ${fmtDateTime(a.updated_at)}` : "Never updated"}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              data-testid={`supplier-mark-contacted-${a.id}`}
                              onClick={() => updateStatus(a.id, "contacted")}
                              className="h-9 px-3 rounded-full border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-2"
                            >
                              <PhoneCall className="w-3.5 h-3.5" /> Mark Contacted
                            </button>
                            <button
                              type="button"
                              data-testid={`supplier-accept-onboard-${a.id}`}
                              onClick={() => acceptOnboard(a)}
                              className="h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] uppercase tracking-[0.25em] font-semibold inline-flex items-center gap-2"
                            >
                              <UserCheck className="w-3.5 h-3.5" /> Accept &amp; Onboard
                            </button>
                            <button
                              type="button"
                              data-testid={`supplier-decline-${a.id}`}
                              onClick={() => updateStatus(a.id, "declined")}
                              className="h-9 px-3 rounded-full border border-zinc-500/40 text-zinc-400 hover:bg-zinc-500/10 text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-2"
                            >
                              <XCircle className="w-3.5 h-3.5" /> Decline
                            </button>
                            <button
                              type="button"
                              data-testid={`supplier-save-notes-${a.id}`}
                              onClick={() => saveNotes(a.id)}
                              className="h-9 px-4 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.25em] font-semibold"
                            >
                              Save notes
                            </button>
                            <button
                              type="button"
                              data-testid={`supplier-delete-${a.id}`}
                              onClick={() => remove(a.id)}
                              className="h-9 px-3 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-2"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
        <SuppliersPanel prefill={onboardPrefill} onPrefillConsumed={() => setOnboardPrefill(null)} />
        <SupplierRequestsPanel />
        <B2BPartnersPanel />
      </main>
      <ConfirmDialog />
    </div>
  );
}

function KpiCard({ label, value, eyebrow, icon, gold, testid }) {
  return (
    <div
      data-testid={testid || `supplier-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={`rounded-2xl border p-4 ${gold ? "border-[#C5A059]/40 bg-[#C5A059]/5" : "border-white/10 bg-[#0d0d0d]"}`}
    >
      <div className={`text-[10px] uppercase tracking-[0.25em] flex items-center gap-2 ${gold ? "text-[#C5A059]" : "text-zinc-500"}`}>
        {icon} {label}
      </div>
      <p className={`text-3xl font-display mt-1 tabular-nums ${gold ? "text-[#C5A059]" : "text-white"}`}>{value}</p>
      {eyebrow && <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{eyebrow}</p>}
    </div>
  );
}
